


import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Pagination,
} from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import StatusDropdown from "../../../../components/StatusDropdown";
import empty from "../../../../assets/empty.png"; // ✅ your empty image
import { useTranslation } from "react-i18next";

const TasksTable = ({ data }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const rowsPerPage = 5;

  useEffect(() => {
    if (data) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // 🔹 Empty state
  if (!data || !data.recent_activity?.length) {
    return (
      <Box
        sx={{
          height: 300,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <img src={empty} alt="No Data" style={{ width: 250, objectFit: "contain" }} />
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 500,
            fontSize: "16px",
            color: "#003149",
            pt: 1,
          }}
        >
          {t("tasks.empty")}
        </CustomText>
      </Box>
    );
  }

  const HaederStyles = {
    color: "#44546F",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    py: "10px",
  };

  const DataStyles = {
    color: "#172B4D",
    fontSize: "12px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    py: "10px",
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).toLowerCase();
    return `${day} ${month} ${year} – ${time}`;
  };

  // Paginated slice
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedData = data.recent_activity.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box sx={{ border: "1px solid #E4E6EF", borderRadius: 2, backgroundColor: "white", p: 0.5 }}>
      <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
        <CustomText
          sx={{
            ...textStyles.h1,
            fontWeight: 600,
            p: 2,
            fontSize: "18px",
            color: "#003149",
            textAlign: "left",
          }}
        >
          {t("tasks.title")}
        </CustomText>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HaederStyles}>{t("tasks.columns.createdDate")}</TableCell>
                <TableCell sx={HaederStyles}>{t("tasks.columns.assignedWorkers")}</TableCell>
                <TableCell sx={HaederStyles}>{t("tasks.columns.count")}</TableCell>
                <TableCell sx={HaederStyles}>{t("tasks.columns.status")}</TableCell>
                <TableCell sx={HaederStyles}>{t("tasks.columns.priority")}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={DataStyles}>{formatDateTime(row.created_date)}</TableCell>
                  <TableCell sx={DataStyles}>{row.assigned_workers}</TableCell>
                  <TableCell sx={DataStyles}>{row.count}</TableCell>
                  <TableCell>
                    <Box sx={{ pointerEvents: "none" }}>
                      <StatusDropdown currentStatus={row.status} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ pointerEvents: "none" }}>
                      <StatusDropdown currentStatus={row.priority} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={Math.ceil(data.recent_activity.length / rowsPerPage)}
            page={page}
            onChange={(e, val) => setPage(val)}
            shape="rounded"
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "Poppins, sans-serif",
                fontSize: 13,
                fontWeight: 500,
              },
              "& .Mui-selected": {
                backgroundColor: "#E9F3FF",
                color: "#006EC2",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TasksTable;
